import React, { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import QBankReferenceEditor from "../components/QBankReferenceEditor";
import styles from "./AdminQBank.module.css";
import AdminQBankReports from "./AdminQBankReports";
import QBankQuestionEntryModal from "../components/QBankQuestionEntryModal";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { isQBankCode, QBankCode, QBankConfig } from "../utils/qbankProfile";
import {
    getReferenceExcerpt,
    getReferencePage,
    getReferenceSource,
    normalizeReferencesForEditor,
    QBankReference,
    QBankReferenceDraft,
    serializeReferencesForSave,
} from "../utils/qbankReferences";

interface Collection {
    id: string;
    title: string;
    year: number;
    month: number;
    description?: string;
    questions_count: number;
    is_published: boolean;
    bank_code?: QBankCode;
}

interface Upload {
    id: string;
    filename: string;
    collection_id: string;
    status: string;
    questions_parsed: number;
    questions_approved: number;
    error_message?: string;
    parsed_questions?: any[];
    raw_text?: string;
    created_at: string;
    updated_at?: string;
}

interface ParsedQuestion {
    question_text: string;
    options: { label: string; text: string }[];
    // Note: correct_answer is determined by Agent4, not during parsing
    specialty: string;
    difficulty: string;
}

interface Question {
    id: string;
    collection_id: string;
    question_text: string;
    options: { label: string; text: string }[];
    correct_answer: string;
    specialty: string;
    difficulty: string;
    explanation?: string;
    question_references?: QBankReference[];
    source_types_used?: string[];
    explainer_status: string;
    explainer_error?: string;
    processing_attempts?: number;
    order_index: number;
    is_published: boolean;
    created_at: string;
    updated_at: string;
}

type QBankAdminTab = "collections" | "uploads" | "reports";

const SDLE_SPECIALTIES = [
    "Endodontics", "Prosthodontics", "Periodontics", "Orthodontics",
    "Pediatric Dentistry", "Restorative Dentistry", "Oral Medicine", "OMFS"
];

const AdminQBank: React.FC = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const qbankParams = new URLSearchParams(location.search);
    const linkedReportId = qbankParams.get("report_id") || undefined;
    const initialBankParam = qbankParams.get("bank") || undefined;
    const [activeBank, setActiveBank] = useState<QBankCode>(isQBankCode(initialBankParam) ? initialBankParam : "sdle");
    const [qbankConfig, setQBankConfig] = useState<QBankConfig | null>(null);
    const specialties = qbankConfig?.specialties.map((specialty) => specialty.name) || SDLE_SPECIALTIES;

    const [collections, setCollections] = useState<Collection[]>([]);
    const [uploads, setUploads] = useState<Upload[]>([]);
    const [activeTab, setActiveTab] = useState<QBankAdminTab>("collections");
    const [mountedQBankTabs, setMountedQBankTabs] = useState<Set<QBankAdminTab>>(
        () => new Set<QBankAdminTab>(["collections"])
    );
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get("tab");
        if (tab === "reports") {
            setActiveTab(tab);
        }
    }, [location.search]);

    useEffect(() => {
        setMountedQBankTabs((prev) => {
            if (prev.has(activeTab)) return prev;
            return new Set(prev).add(activeTab);
        });
    }, [activeTab]);

    // Collection form
    const [showNewCollection, setShowNewCollection] = useState(false);
    const [showQuestionEntry, setShowQuestionEntry] = useState(false);
    const [newCollectionYear, setNewCollectionYear] = useState(new Date().getFullYear());
    const [newCollectionMonth, setNewCollectionMonth] = useState(new Date().getMonth() + 1);
    const [newCollectionTitle, setNewCollectionTitle] = useState("");
    const [newCollectionDescription, setNewCollectionDescription] = useState("");
    const [creatingCollection, setCreatingCollection] = useState(false);
    const [collectionError, setCollectionError] = useState("");
    const collectionSubmissionRef = useRef(false);
    const existingCollection = collections.find((collection) =>
        (!collection.bank_code || collection.bank_code === activeBank) &&
        collection.year === newCollectionYear && collection.month === newCollectionMonth
    );

    useEffect(() => {
        setCollectionError("");
    }, [showNewCollection, activeBank, newCollectionYear, newCollectionMonth]);

    // Update title when year/month changes if user hasn't typed a custom title
    useEffect(() => {
        if (!showNewCollection) {
            setNewCollectionTitle(`${getMonthName(newCollectionMonth)} ${newCollectionYear}`);
            setNewCollectionDescription("");
        }
    }, [showNewCollection, newCollectionMonth, newCollectionYear]);

    // Upload form
    const [selectedCollectionForUpload, setSelectedCollectionForUpload] = useState("");
    const [uploading, setUploading] = useState(false);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [sourceRightsConfirmed, setSourceRightsConfirmed] = useState(false);
    const [sourceType, setSourceType] = useState("original");
    const [sourceReference, setSourceReference] = useState("");

    // Text upload modal
    const [showTextUpload, setShowTextUpload] = useState(false);
    // Initialize from localStorage to persist text across rerenders
    const [textUploadContent, setTextUploadContent] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(`qbank_${isQBankCode(initialBankParam) ? initialBankParam : "sdle"}_draft_text`) || '';
        }
        return '';
    });

    // Telegram import state
    const [showTelegramImport, setShowTelegramImport] = useState(false);
    const [telegramSession, setTelegramSession] = useState<any>(null);
    const [telegramLoading, setTelegramLoading] = useState(false);
    const [telegramError, setTelegramError] = useState("");
    // Auth form
    const [tgApiId, setTgApiId] = useState("");
    const [tgApiHash, setTgApiHash] = useState("");
    const [tgPhone, setTgPhone] = useState("");
    const [tgCode, setTgCode] = useState("");
    const [tg2faPassword, setTg2faPassword] = useState("");
    const [tgAuthSessionId, setTgAuthSessionId] = useState("");
    // Import form
    const [tgChannel, setTgChannel] = useState(() => {
        if (typeof window !== 'undefined') return localStorage.getItem(`qbank_${isQBankCode(initialBankParam) ? initialBankParam : "sdle"}_last_telegram_channel`) || '';
        return '';
    });
    const [tgChannelInfo, setTgChannelInfo] = useState<any>(null);
    const [tgDateFrom, setTgDateFrom] = useState("");
    const [tgDateTo, setTgDateTo] = useState("");
    // Preview
    const [tgPreview, setTgPreview] = useState<any>(null);
    const [tgPreviewText, setTgPreviewText] = useState(() => {
        if (typeof window !== 'undefined') return localStorage.getItem(`qbank_${isQBankCode(initialBankParam) ? initialBankParam : "sdle"}_telegram_draft`) || '';
        return '';
    });

    // Review state
    const [reviewingUpload, setReviewingUpload] = useState<Upload | null>(null);
    const [editableQuestions, setEditableQuestions] = useState<ParsedQuestion[]>([]);

    // Global search state
    const [globalSearch, setGlobalSearch] = useState("");
    const [searchResults, setSearchResults] = useState<Question[]>([]);
    const [searching, setSearching] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [expandedSearchResults, setExpandedSearchResults] = useState<Set<string>>(new Set());

    // Edit modal state for search results
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [editForm, setEditForm] = useState({
        question_text: "",
        options: [] as { label: string; text: string }[],
        correct_answer: "",
        specialty: "",
        difficulty: "",
        explanation: "",
        question_references: [] as QBankReferenceDraft[],
    });
    const [savingQuestion, setSavingQuestion] = useState(false);

    // Polling state for parsing uploads
    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    useEffect(() => {
        loadData();
    }, [user?.id, activeBank]);

    // Cleanup search timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Auto-save text to localStorage (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (textUploadContent) {
                localStorage.setItem(`qbank_${activeBank}_draft_text`, textUploadContent);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [textUploadContent, activeBank]);

    // Auto-save telegram draft to localStorage (debounced)
    useEffect(() => {
        if (!tgPreviewText) return;
        const timeoutId = setTimeout(() => {
            localStorage.setItem(`qbank_${activeBank}_telegram_draft`, tgPreviewText);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [tgPreviewText, activeBank]);

    // Polling for uploads in 'parsing' status
    useEffect(() => {
        const hasParsingUploads = uploads.some(u => u.status === 'parsing');

        if (hasParsingUploads) {
            // Start polling every 3 seconds
            pollingRef.current = setInterval(() => {
                loadUploadsOnly();
            }, 3000);
        } else {
            // Stop polling
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [uploads]);

    // Load only uploads (for polling)
    const loadUploadsOnly = async () => {
        if (!user?.id) return;
        try {
            const uploadsRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/uploads?user_id=${user.id}&bank=${activeBank}`
            );
            if (uploadsRes.ok) {
                setUploads(await uploadsRes.json());
            }
        } catch (err) {
            console.error("Failed to refresh uploads:", err);
        }
    };

    const loadData = async (showLoading = true) => {
        if (!user?.id) return;
        if (showLoading) setLoading(true);
        try {
            const configRes = await fetch(`${apiUrl}/api/qbank/config?bank=${activeBank}`);
            if (configRes.ok) setQBankConfig(await configRes.json());
            // Load collections
            const collectionsRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/collections?user_id=${user.id}&bank=${activeBank}`
            );
            if (collectionsRes.ok) {
                setCollections(await collectionsRes.json());
            }

            // Load uploads
            const uploadsRes = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/uploads?user_id=${user.id}&bank=${activeBank}`
            );
            if (uploadsRes.ok) {
                setUploads(await uploadsRes.json());
            }
        } catch (err) {
            console.error("Failed to load admin data:", err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const getMonthName = (month: number) => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[month - 1] || "Unknown";
    };

    // Global search with debounce
    const handleGlobalSearch = (query: string) => {
        setGlobalSearch(query);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!query.trim()) {
            setSearchResults([]);
            setSearching(false);
            return;
        }

        // Debounce search
        searchTimeoutRef.current = setTimeout(async () => {
            if (!user?.id) return;
            setSearching(true);
            try {
                const res = await authenticatedFetch(
                    `${apiUrl}/api/admin/qbank/questions?user_id=${user.id}&bank=${activeBank}&search=${encodeURIComponent(query)}&limit=50`
                );
                if (res.ok) {
                    setSearchResults(await res.json());
                }
            } catch (err) {
                console.error("Search failed:", err);
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    // Toggle expand search result
    const toggleSearchResultExpand = (questionId: string) => {
        setExpandedSearchResults((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(questionId)) {
                newSet.delete(questionId);
            } else {
                newSet.add(questionId);
            }
            return newSet;
        });
    };

    // Open edit modal for a search result question
    const openEditModal = (question: Question, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingQuestion(question);
        setEditForm({
            question_text: question.question_text || "",
            options: question.options || [],
            correct_answer: question.correct_answer || "",
            specialty: question.specialty || "",
            difficulty: question.difficulty || "",
            explanation: question.explanation || "",
            question_references: normalizeReferencesForEditor(question.question_references),
        });
    };

    const closeEditModal = () => {
        setEditingQuestion(null);
    };

    const buildQuestionUpdatePayload = () => ({
        question_text: editForm.question_text,
        options: editForm.options,
        correct_answer: editForm.correct_answer,
        specialty: editForm.specialty,
        difficulty: editForm.difficulty,
        explanation: editForm.explanation,
        question_references: serializeReferencesForSave(editForm.question_references),
    });

    // Save question changes
    const saveQuestion = async () => {
        if (!editingQuestion || !user?.id) return;
        setSavingQuestion(true);

        try {
            const questionUpdates = buildQuestionUpdatePayload();
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/questions/${editingQuestion.id}?bank=${activeBank}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        ...questionUpdates,
                    }),
                }
            );

            if (res.ok) {
                // Update in search results
                setSearchResults((prev) =>
                    prev.map((q) =>
                        q.id === editingQuestion.id
                            ? { ...q, ...questionUpdates }
                            : q
                    )
                );
                closeEditModal();
                (window as any).showNotification?.("Question updated", "success");
            }
        } catch (err) {
            console.error("Failed to save question:", err);
            (window as any).showNotification?.("Failed to save question", "error");
        } finally {
            setSavingQuestion(false);
        }
    };

    // Toggle publish for a search result question
    const togglePublishQuestion = async (question: Question, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;

        const newPublishState = !question.is_published;

        // Optimistic update
        setSearchResults((prev) =>
            prev.map((q) =>
                q.id === question.id ? { ...q, is_published: newPublishState } : q
            )
        );

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${question.id}?bank=${activeBank}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    is_published: newPublishState,
                }),
            });
        } catch (err) {
            console.error("Failed to toggle publish:", err);
            // Revert on failure
            setSearchResults((prev) =>
                prev.map((q) =>
                    q.id === question.id ? { ...q, is_published: question.is_published } : q
                )
            );
        }
    };

    // Delete a search result question
    const deleteQuestion = async (questionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;
        if (!window.confirm("Are you sure you want to delete this question? This cannot be undone.")) return;

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${questionId}?bank=${activeBank}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            setSearchResults((prev) => prev.filter((q) => q.id !== questionId));
            (window as any).showNotification?.("Question deleted", "success");
        } catch (err) {
            console.error("Failed to delete question:", err);
            (window as any).showNotification?.("Failed to delete question", "error");
        }
    };

    // Retry a failed question
    const retryQuestion = async (questionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.id) return;

        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions/${questionId}/retry?bank=${activeBank}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id }),
            });
            // Update status in results
            setSearchResults((prev) =>
                prev.map((q) =>
                    q.id === questionId ? { ...q, explainer_status: "pending" } : q
                )
            );
            (window as any).showNotification?.("Question queued for retry", "success");
        } catch (err) {
            console.error("Failed to retry question:", err);
        }
    };

    // Copy question ID
    const copyId = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(id);
        (window as any).showNotification?.("ID copied to clipboard", "success");
    };

    // Get status color
    const getStatusColor = (status: string): string => {
        const colors: Record<string, string> = {
            pending: "#f59e0b",
            processing: "#7b5cff",
            completed: "#22c55e",
            failed: "#ef4444",
        };
        return colors[status] || "#888";
    };

    const createCollection = async () => {
        if (!user?.id || collectionSubmissionRef.current || existingCollection) return;
        collectionSubmissionRef.current = true;
        setCreatingCollection(true);
        setCollectionError("");
        try {
            // Use custom title if provided, else default
            const title = newCollectionTitle || `${getMonthName(newCollectionMonth)} ${newCollectionYear}`;

            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/collections`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    year: newCollectionYear,
                    month: newCollectionMonth,
                    title: title,
                    description: newCollectionDescription,
                    bank_code: activeBank,
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.error || "Could not create the collection. Please try again.");
            }
            loadData();
            setShowNewCollection(false);
        } catch (err) {
            console.error("Failed to create collection:", err);
            setCollectionError(err instanceof Error ? err.message : "Could not create the collection. Please try again.");
        } finally {
            collectionSubmissionRef.current = false;
            setCreatingCollection(false);
        }
    };

    const togglePublish = async (collectionId: string, currentState: boolean) => {
        if (!user?.id) return;
        try {
            await authenticatedFetch(`${apiUrl}/api/admin/qbank/collections/${collectionId}?bank=${activeBank}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, is_published: !currentState }),
            });
            loadData();
        } catch (err) {
            console.error("Failed to toggle publish:", err);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length || !selectedCollectionForUpload || !user?.id) return;

        const file = e.target.files[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_id", user.id);
        formData.append("collection_id", selectedCollectionForUpload);
        formData.append("source_type", sourceType);
        formData.append("source_reference", sourceReference);
        formData.append("rights_confirmed", String(sourceRightsConfirmed));

        if (activeBank === "smle" && !sourceRightsConfirmed) {
            (window as any).showNotification?.("Confirm that this SMLE source is permitted before uploading.", "error");
            return;
        }

        setUploading(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/upload`, {
                method: "POST",
                body: formData,
            });
            if (res.ok) {
                loadData();
                setActiveTab("uploads");
            }
        } catch (err) {
            console.error("Failed to upload:", err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleTextUpload = async () => {
        if (!textUploadContent.trim() || !selectedCollectionForUpload || !user?.id) return;

        setUploading(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/upload`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    collection_id: selectedCollectionForUpload,
                    text: textUploadContent,
                    source_type: sourceType,
                    source_reference: sourceReference,
                    rights_confirmed: sourceRightsConfirmed,
                }),
            });
            if (res.ok) {
                loadData();
                setActiveTab("uploads");
                setShowTextUpload(false);
                setTextUploadContent("");
                // Clear localStorage on successful upload
                localStorage.removeItem(`qbank_${activeBank}_draft_text`);
            } else {
                const error = await res.json();
                alert(`Upload failed: ${error.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error("Failed to upload text:", err);
            alert(`Upload failed: ${err}`);
        } finally {
            setUploading(false);
        }
    };

    // Telegram import handlers
    const loadTelegramSession = async () => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/telegram-import/session?user_id=${user.id}`
            );
            if (res.ok) {
                const data = await res.json();
                setTelegramSession(data.session);
            }
        } catch (err) {
            console.error("Failed to load telegram session:", err);
        }
    };

    const handleTelegramAuth = async () => {
        if (!user?.id || !tgApiId || !tgApiHash || !tgPhone) return;
        setTelegramLoading(true);
        setTelegramError("");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/session/auth`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    api_id: parseInt(tgApiId),
                    api_hash: tgApiHash,
                    phone_number: tgPhone,
                }),
            });
            const data = await res.json();
            if (res.ok) {
                setTgAuthSessionId(data.session_id);
                setTelegramSession({ auth_status: "awaiting_code" });
            } else {
                setTelegramError(data.error || "Failed to send code");
            }
        } catch (err) {
            setTelegramError(`Failed: ${err}`);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleTelegramVerifyCode = async () => {
        if (!user?.id || !tgCode || !tgAuthSessionId) return;
        setTelegramLoading(true);
        setTelegramError("");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/session/verify-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    session_id: tgAuthSessionId,
                    code: tgCode,
                }),
            });
            const data = await res.json();
            if (res.ok) {
                if (data.auth_status === "authenticated") {
                    await loadTelegramSession();
                } else {
                    setTelegramSession({ auth_status: data.auth_status });
                }
            } else {
                setTelegramError(data.error || "Invalid code");
            }
        } catch (err) {
            setTelegramError(`Failed: ${err}`);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleTelegramVerify2FA = async () => {
        if (!user?.id || !tg2faPassword || !tgAuthSessionId) return;
        setTelegramLoading(true);
        setTelegramError("");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/session/verify-2fa`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    session_id: tgAuthSessionId,
                    password: tg2faPassword,
                }),
            });
            const data = await res.json();
            if (res.ok) {
                await loadTelegramSession();
            } else {
                setTelegramError(data.error || "Invalid password");
            }
        } catch (err) {
            setTelegramError(`Failed: ${err}`);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleTelegramDisconnect = async () => {
        if (!user?.id || !window.confirm("Disconnect Telegram account?")) return;
        setTelegramLoading(true);
        try {
            await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/session?user_id=${user.id}`, {
                method: "DELETE",
            });
            setTelegramSession(null);
            setTgChannelInfo(null);
            setTgPreview(null);
            setTgPreviewText("");
            localStorage.removeItem(`qbank_${activeBank}_telegram_draft`);
        } catch (err) {
            console.error("Failed to disconnect:", err);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleResolveChannel = async () => {
        if (!user?.id || !tgChannel.trim()) return;
        setTelegramLoading(true);
        setTgChannelInfo(null);
        setTelegramError("");
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/resolve-channel`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ user_id: user.id, channel: tgChannel.trim() }),
            });
            const data = await res.json();
            if (res.ok) {
                setTgChannelInfo(data);
                localStorage.setItem(`qbank_${activeBank}_last_telegram_channel`, tgChannel.trim());
            } else {
                setTelegramError(data.error || "Channel not found");
            }
        } catch (err) {
            setTelegramError(`Failed: ${err}`);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleFetchMessages = async () => {
        if (!user?.id || !tgChannel.trim() || !tgDateFrom || !tgDateTo) return;
        setTelegramLoading(true);
        setTelegramError("");
        setTgPreview(null);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/telegram-import/fetch-messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    channel: tgChannel.trim(),
                    date_from: tgDateFrom,
                    date_to: tgDateTo,
                }),
            });
            const data = await res.json();
            if (res.ok) {
                setTgPreview(data);
                setTgPreviewText(data.text_content || "");
            } else {
                setTelegramError(data.error || "Failed to fetch messages");
            }
        } catch (err) {
            setTelegramError(`Failed: ${err}`);
        } finally {
            setTelegramLoading(false);
        }
    };

    const handleTelegramSubmit = async () => {
        if (!tgPreviewText.trim() || !selectedCollectionForUpload || !user?.id) return;
        setUploading(true);
        try {
            // Build a descriptive source name
            const channelName = tgChannelInfo?.username || tgChannel.trim().replace("@", "");
            const sourceText = `[Source: Telegram @${channelName} ${tgDateFrom} to ${tgDateTo}]\n\n${tgPreviewText}`;

            const res = await authenticatedFetch(`${apiUrl}/api/admin/qbank/upload`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user.id,
                    collection_id: selectedCollectionForUpload,
                    text: sourceText,
                    source_type: "telegram",
                    source_reference: `@${channelName}`,
                    source_metadata: { date_from: tgDateFrom, date_to: tgDateTo },
                    rights_confirmed: sourceRightsConfirmed,
                }),
            });
            if (res.ok) {
                loadData();
                setActiveTab("uploads");
                setShowTelegramImport(false);
                setTgPreview(null);
                setTgPreviewText("");
                localStorage.removeItem(`qbank_${activeBank}_telegram_draft`);
            } else {
                const error = await res.json();
                alert(`Upload failed: ${error.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error("Failed to submit telegram import:", err);
            alert(`Upload failed: ${err}`);
        } finally {
            setUploading(false);
        }
    };

    // Retry a failed upload
    const retryUpload = async (uploadId: string) => {
        if (!user?.id) return;
        setRetrying(uploadId);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/uploads/${uploadId}/retry`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );
            if (res.ok) {
                loadData();
            } else {
                const error = await res.json();
                alert(`Retry failed: ${error.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error("Failed to retry upload:", err);
            alert(`Retry failed: ${err}`);
        } finally {
            setRetrying(null);
        }
    };

    const openReview = async (upload: Upload) => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/uploads/${upload.id}?user_id=${user.id}`
            );
            if (res.ok) {
                const fullUpload = await res.json();
                setReviewingUpload(fullUpload);
                setEditableQuestions(fullUpload.parsed_questions || []);
            }
        } catch (err) {
            console.error("Failed to load upload:", err);
        }
    };

    const approveQuestions = async () => {
        if (!reviewingUpload || !user?.id) return;
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/uploads/${reviewingUpload.id}/approve`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        questions: editableQuestions,
                    }),
                }
            );
            if (res.ok) {
                const result = await res.json();
                alert(`Created ${result.questions_created} questions. Agent4 processing started.`);
                setReviewingUpload(null);
                loadData();
            }
        } catch (err) {
            console.error("Failed to approve questions:", err);
        }
    };

    const updateQuestion = (index: number, field: keyof ParsedQuestion, value: any) => {
        setEditableQuestions((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const openCollectionDetail = (collection: Collection) => {
        navigate(`/admin/qbank/collection/${collection.id}?bank=${activeBank}`);
    };

    const getStatusBadge = (status: string) => {
        const colors: Record<string, string> = {
            uploaded: "#888",
            parsing: "#f59e0b",
            pending_review: "#00d9ff",
            processing: "#7b5cff",
            completed: "#22c55e",
            failed: "#ef4444",
        };
        return (
            <span
                className={styles.statusBadge}
                style={{ background: colors[status] || "#888" }}
            >
                {status.replace("_", " ")}
            </span>
        );
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>{activeBank.toUpperCase()} QBank Management</h1>
                    <select
                        className={styles.select}
                        value={activeBank}
                        onChange={(event) => {
                            const nextBank = event.target.value as QBankCode;
                            localStorage.setItem(`qbank_${activeBank}_draft_text`, textUploadContent);
                            localStorage.setItem(`qbank_${activeBank}_telegram_draft`, tgPreviewText);
                            setActiveBank(nextBank);
                            setTextUploadContent(localStorage.getItem(`qbank_${nextBank}_draft_text`) || "");
                            setTgPreviewText(localStorage.getItem(`qbank_${nextBank}_telegram_draft`) || "");
                            setTgChannel(localStorage.getItem(`qbank_${nextBank}_last_telegram_channel`) || "");
                            setSelectedCollectionForUpload("");
                            setSearchResults([]);
                            setGlobalSearch("");
                            setSourceRightsConfirmed(false);
                            const params = new URLSearchParams(location.search);
                            params.set("bank", nextBank);
                            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                        }}
                        aria-label="QBank"
                    >
                        <option value="sdle">SDLE</option>
                        <option value="smle">SMLE</option>
                    </select>
                </div>
                <div className={styles.headerActions}>
                    <button
                        className={styles.secondaryButton}
                        onClick={() => setShowQuestionEntry(true)}
                        disabled={collections.length === 0}
                    >
                        + Add Questions
                    </button>
                    <button
                        className={styles.primaryButton}
                        onClick={() => setShowNewCollection(true)}
                    >
                        + New Collection
                    </button>
                </div>
            </div>

            {/* Global Search */}
            <div className={styles.globalSearchContainer}>
                <div className={styles.globalSearchInputWrapper}>
                    <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className={styles.globalSearchInput}
                        placeholder="Search all questions across collections..."
                        value={globalSearch}
                        onChange={(e) => handleGlobalSearch(e.target.value)}
                    />
                    {globalSearch && (
                        <button
                            className={styles.clearGlobalSearch}
                            onClick={() => {
                                setGlobalSearch("");
                                setSearchResults([]);
                            }}
                        >
                            ×
                        </button>
                    )}
                    {searching && <span className={styles.searchSpinner} />}
                </div>

                {/* Search Results */}
                {globalSearch && (
                    <div className={styles.searchResultsPanel}>
                        {searching ? (
                            <p className={styles.searchStatus}>Searching...</p>
                        ) : searchResults.length === 0 ? (
                            <p className={styles.searchStatus}>No questions found matching "{globalSearch}"</p>
                        ) : (
                            <>
                                <div className={styles.searchResultsHeader}>
                                    <p className={styles.searchResultsCount}>
                                        Found {searchResults.length} question{searchResults.length !== 1 ? 's' : ''}
                                    </p>
                                    <div className={styles.searchResultsActions}>
                                        <button
                                            className={styles.linkButton}
                                            onClick={() => setExpandedSearchResults(new Set(searchResults.map(q => q.id)))}
                                        >
                                            Expand All
                                        </button>
                                        <button
                                            className={styles.linkButton}
                                            onClick={() => setExpandedSearchResults(new Set())}
                                        >
                                            Collapse All
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.searchResultsList}>
                                    {searchResults.map((q) => {
                                        const isExpanded = expandedSearchResults.has(q.id);
                                        return (
                                            <div
                                                key={q.id}
                                                className={`${styles.searchResultItem} ${isExpanded ? styles.expanded : ''}`}
                                            >
                                                {/* Collapsed Header */}
                                                <div
                                                    className={styles.searchResultItemHeader}
                                                    onClick={() => toggleSearchResultExpand(q.id)}
                                                >
                                                    <div className={styles.searchResultInfo}>
                                                        <div className={styles.searchResultTopRow}>
                                                            <span className={styles.searchResultCollection}>
                                                                {(q as any).qbank_collections?.title || 'Unknown'}
                                                            </span>
                                                            <span
                                                                className={styles.searchResultId}
                                                                onClick={(e) => copyId(q.id, e)}
                                                                title="Click to copy ID"
                                                            >
                                                                {q.id.substring(0, 8)}...
                                                            </span>
                                                        </div>
                                                        <p className={styles.searchResultPreview}>
                                                            {q.question_text && q.question_text.length > 120
                                                                ? `${q.question_text.substring(0, 120)}...`
                                                                : q.question_text || "(No text)"}
                                                        </p>
                                                    </div>
                                                    <div className={styles.searchResultMeta}>
                                                        <span
                                                            className={styles.statusBadgeSmall}
                                                            style={{ background: getStatusColor(q.explainer_status) }}
                                                        >
                                                            {q.explainer_status}
                                                        </span>
                                                        {q.explainer_status === "completed" && (
                                                            <span className={`${styles.publishBadgeSmall} ${q.is_published ? styles.published : styles.draft}`}>
                                                                {q.is_published ? "Published" : "Draft"}
                                                            </span>
                                                        )}
                                                        <span className={styles.expandIcon}>
                                                            {isExpanded ? "−" : "+"}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Expanded Content */}
                                                {isExpanded && (
                                                    <div className={styles.searchResultContent}>
                                                        <div className={styles.searchResultTags}>
                                                            <span className={styles.tag}>{q.specialty || "Unknown"}</span>
                                                            <span className={styles.tag}>{q.difficulty || "Unknown"}</span>
                                                        </div>

                                                        <p className={styles.fullQuestionText}>
                                                            {q.question_text || "No question text"}
                                                        </p>

                                                        <div className={styles.optionsList}>
                                                            {(q.options || []).map((opt) => (
                                                                <div
                                                                    key={opt.label}
                                                                    className={`${styles.option} ${q.correct_answer === opt.label && q.explainer_status === "completed"
                                                                        ? styles.correct
                                                                        : ""
                                                                        }`}
                                                                >
                                                                    <span className={styles.optionLabel}>{opt.label}</span>
                                                                    <span className={styles.optionText}>{opt.text}</span>
                                                                    {q.correct_answer === opt.label && q.explainer_status === "completed" && (
                                                                        <span className={styles.correctBadge}>Correct</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {q.explainer_status === "completed" && q.explanation && (
                                                            <div className={styles.explanationBox}>
                                                                <h4>Explanation</h4>
                                                                <p>{q.explanation}</p>
                                                            </div>
                                                        )}

                                                        {q.question_references && q.question_references.length > 0 && (
                                                            <div className={styles.referencesBox}>
                                                                <h4>References</h4>
                                                                {q.question_references.map((reference, index) => {
                                                                    const excerpt = getReferenceExcerpt(reference);
                                                                    const source = getReferenceSource(reference);
                                                                    const page = getReferencePage(reference);

                                                                    return (
                                                                        <div key={index} className={styles.referenceItem}>
                                                                            {excerpt && (
                                                                                <div className={styles.referenceExcerpt}>
                                                                                    "{excerpt}"
                                                                                </div>
                                                                            )}
                                                                            <div className={styles.referenceSource}>
                                                                                {source}
                                                                                {page && <span>, p. {page}</span>}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {q.explainer_status === "failed" && (
                                                            <div className={styles.errorBox}>
                                                                <p>
                                                                    <strong>Error:</strong> {q.explainer_error || "Unknown error"}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Action Buttons */}
                                                        <div className={styles.searchResultActions}>
                                                            <button
                                                                className={styles.actionButton}
                                                                onClick={(e) => openEditModal(q, e)}
                                                            >
                                                                Edit
                                                            </button>
                                                            {q.explainer_status === "completed" && (
                                                                <button
                                                                    className={`${styles.actionButton} ${q.is_published ? styles.unpublishBtn : styles.publishBtn}`}
                                                                    onClick={(e) => togglePublishQuestion(q, e)}
                                                                >
                                                                    {q.is_published ? "Unpublish" : "Publish"}
                                                                </button>
                                                            )}
                                                            {q.explainer_status === "failed" && (
                                                                <button
                                                                    className={`${styles.actionButton} ${styles.retryBtn}`}
                                                                    onClick={(e) => retryQuestion(q.id, e)}
                                                                >
                                                                    Retry
                                                                </button>
                                                            )}
                                                            <button
                                                                className={`${styles.actionButton} ${styles.viewCollectionBtn}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/admin/qbank/collection/${q.collection_id}`);
                                                                }}
                                                            >
                                                                View in Collection
                                                            </button>
                                                            <button
                                                                className={`${styles.actionButton} ${styles.deleteBtn}`}
                                                                onClick={(e) => deleteQuestion(q.id, e)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === "collections" ? styles.tabActive : ""}`}
                    onClick={() => setActiveTab("collections")}
                >
                    Collections ({collections.length})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === "uploads" ? styles.tabActive : ""}`}
                    onClick={() => setActiveTab("uploads")}
                >
                    Uploads ({uploads.length})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === "reports" ? styles.tabActive : ""}`}
                    onClick={() => setActiveTab("reports")}
                >
                    Reports
                </button>
            </div>

            {/* Content */}
            {mountedQBankTabs.has("collections") && (
                <div
                    className={styles.section}
                    style={{ display: activeTab === "collections" ? undefined : "none" }}
                >
                    {/* Upload bar */}
                    <div className={styles.uploadBar}>
                        {activeBank === "smle" && (
                            <div className={styles.sourceRightsPanel}>
                                <div className={styles.sourceRightsCopy}>
                                    <label className={styles.sourceRightsLabel}>
                                        <input
                                            type="checkbox"
                                            checked={sourceRightsConfirmed}
                                            onChange={(event) => setSourceRightsConfirmed(event.target.checked)}
                                        />
                                        <span>I confirm this source is original, licensed, user-authored, or otherwise permitted.</span>
                                    </label>
                                    <small>Do not upload recalled or reconstructed official SMLE examination items.</small>
                                </div>
                                <select className={styles.select} aria-label="SMLE source type" value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
                                    <option value="original">Original</option>
                                    <option value="licensed">Licensed</option>
                                    <option value="user_authored">User-authored</option>
                                    <option value="permitted">Otherwise permitted</option>
                                </select>
                                <input className={styles.input} aria-label="Source or license reference" value={sourceReference} onChange={(event) => setSourceReference(event.target.value)} placeholder="Source or license reference" />
                            </div>
                        )}
                        <div className={styles.uploadControls}>
                            <select
                                className={styles.select}
                                aria-label="Collection for upload"
                                value={selectedCollectionForUpload}
                                onChange={(e) => setSelectedCollectionForUpload(e.target.value)}
                            >
                                <option value="">Select collection for upload</option>
                                {collections.map((c) => (
                                    <option key={c.id} value={c.id}>{c.title}</option>
                                ))}
                            </select>
                            <input
                                type="file"
                                accept=".pdf"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                disabled={!selectedCollectionForUpload || uploading || (activeBank === "smle" && !sourceRightsConfirmed)}
                                style={{ display: "none" }}
                            />
                            <button
                                className={styles.uploadButton}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={!selectedCollectionForUpload || uploading || (activeBank === "smle" && !sourceRightsConfirmed)}
                            >
                                {uploading ? "Uploading..." : "Upload PDF"}
                            </button>
                            <button
                                className={styles.uploadButton}
                                onClick={() => setShowTextUpload(true)}
                                disabled={!selectedCollectionForUpload || uploading || (activeBank === "smle" && !sourceRightsConfirmed)}
                            >
                                Upload Text
                            </button>
                            <button
                                className={styles.uploadButton}
                                onClick={() => {
                                    setTelegramError("");
                                    loadTelegramSession();
                                    setShowTelegramImport(true);
                                }}
                                disabled={!selectedCollectionForUpload || uploading || (activeBank === "smle" && !sourceRightsConfirmed)}
                            >
                                Import from Telegram
                            </button>
                        </div>
                    </div>

                    {/* Collections list */}
                    <div className={styles.grid}>
                        {collections.map((collection) => (
                            <div key={collection.id} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <h3>{collection.title}</h3>
                                    <span
                                        className={`${styles.publishBadge} ${collection.is_published ? styles.published : styles.draft
                                            }`}
                                    >
                                        {collection.is_published ? "Published" : "Draft"}
                                    </span>
                                </div>
                                <p className={styles.cardMeta}>
                                    {collection.questions_count} questions
                                </p>
                                <div className={styles.cardActions}>
                                    <button
                                        className={styles.linkButton}
                                        onClick={() => openCollectionDetail(collection)}
                                    >
                                        Manage
                                    </button>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={() => togglePublish(collection.id, collection.is_published)}
                                    >
                                        {collection.is_published ? "Unpublish" : "Publish"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {mountedQBankTabs.has("uploads") && (
                <div
                    className={styles.section}
                    style={{ display: activeTab === "uploads" ? undefined : "none" }}
                >
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Filename</th>
                                    <th>Collection</th>
                                    <th>Status</th>
                                    <th>Questions</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploads.map((upload) => (
                                    <tr key={upload.id}>
                                        <td>
                                            <div className={styles.uploadFilename}>
                                                {upload.filename}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={styles.collectionTag}>
                                                {collections.find(c => c.id === upload.collection_id)?.title || 'Unknown'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.statusCell}>
                                                {getStatusBadge(upload.status)}
                                                {upload.status === 'parsing' && (
                                                    <span className={styles.parsingIndicator}>⏳</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {upload.questions_parsed > 0 && (
                                                <span>{upload.questions_approved || 0}/{upload.questions_parsed}</span>
                                            )}
                                        </td>
                                        <td>{new Date(upload.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {upload.status === "pending_review" && (
                                                <button
                                                    className={styles.linkButton}
                                                    onClick={() => openReview(upload)}
                                                >
                                                    Review
                                                </button>
                                            )}
                                            {upload.status === "failed" && (
                                                <div className={styles.failedActions}>
                                                    <button
                                                        className={upload.questions_approved > 0 ? styles.completeButton : styles.retryButton}
                                                        onClick={() => retryUpload(upload.id)}
                                                        disabled={retrying === upload.id || (!upload.raw_text && upload.questions_approved === 0)}
                                                        title={
                                                            upload.questions_approved > 0
                                                                ? `${upload.questions_approved} questions already done - click to mark complete`
                                                                : upload.raw_text
                                                                    ? 'Retry parsing'
                                                                    : 'No saved text - re-upload required'
                                                        }
                                                    >
                                                        {retrying === upload.id
                                                            ? 'Processing...'
                                                            : upload.questions_approved > 0
                                                                ? '✓ Mark Complete'
                                                                : '↻ Retry'}
                                                    </button>
                                                    {upload.error_message && upload.questions_approved === 0 && (
                                                        <span
                                                            className={styles.errorHint}
                                                            title={upload.error_message}
                                                        >
                                                            ⚠ Error
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {mountedQBankTabs.has("reports") && (
                <div
                    className={styles.section}
                    style={{ display: activeTab === "reports" ? undefined : "none" }}
                >
                    <AdminQBankReports selectedReportId={linkedReportId} bank={activeBank} specialties={specialties} />
                </div>
            )}


            {/* Manual / structured question entry */}
            {showQuestionEntry && user?.id && (
                <QBankQuestionEntryModal
                    userId={user.id}
                    bankCode={activeBank}
                    specialties={specialties}
                    defaultSpecialty={qbankConfig?.default_specialty || specialties[0]}
                    optionCount={qbankConfig?.option_count ?? null}
                    collections={collections}
                    initialCollectionId={selectedCollectionForUpload || undefined}
                    onClose={() => setShowQuestionEntry(false)}
                    onImported={async () => {
                        await loadData(false);
                    }}
                />
            )}

            {/* New Collection Modal */}
            {showNewCollection && (
                <div className={styles.modal} onClick={() => !creatingCollection && setShowNewCollection(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <h2>New Collection</h2>
                        <p>Each bank can have one collection per month.</p>
                        <div className={styles.formRow}>
                            <label>Year</label>
                            <input
                                type="number"
                                aria-label="Collection year"
                                disabled={creatingCollection}
                                value={newCollectionYear}
                                onChange={(e) => setNewCollectionYear(parseInt(e.target.value))}
                                className={styles.input}
                            />
                        </div>
                        <div className={styles.formRow}>
                            <label>Month</label>
                            <select
                                aria-label="Collection month"
                                disabled={creatingCollection}
                                value={newCollectionMonth}
                                onChange={(e) => setNewCollectionMonth(parseInt(e.target.value))}
                                className={styles.select}
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <option key={m} value={m}>{getMonthName(m)}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.formRow}>
                            <label>Title</label>
                            <input
                                type="text"
                                aria-label="Collection title"
                                disabled={creatingCollection}
                                value={newCollectionTitle}
                                onChange={(e) => setNewCollectionTitle(e.target.value)}
                                className={styles.input}
                                placeholder="e.g. November 2024"
                            />
                        </div>
                        <div className={styles.formRow}>
                            <label>Description (Optional)</label>
                            <textarea
                                disabled={creatingCollection}
                                value={newCollectionDescription}
                                onChange={(e) => setNewCollectionDescription(e.target.value)}
                                className={styles.textarea}
                                rows={3}
                                placeholder="Description of this collection..."
                            />
                        </div>
                        {existingCollection && (
                            <div className={styles.errorBox} role="alert">
                                <p>{activeBank.toUpperCase()} already has a collection for {getMonthName(newCollectionMonth)} {newCollectionYear}: {existingCollection.title}. Choose another month or add questions to the existing collection.</p>
                                <button className={styles.secondaryButton} onClick={() => openCollectionDetail(existingCollection)}>
                                    Open existing collection
                                </button>
                            </div>
                        )}
                        {collectionError && <div className={styles.errorBox} role="alert"><p>{collectionError}</p></div>}
                        <div className={styles.modalActions}>
                            <button
                                className={styles.secondaryButton}
                                disabled={creatingCollection}
                                onClick={() => setShowNewCollection(false)}
                            >
                                Cancel
                            </button>
                            <button className={styles.primaryButton} onClick={createCollection} disabled={creatingCollection || !!existingCollection}>
                                {creatingCollection ? "Creating…" : "Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Text Upload Modal */}
            {showTextUpload && (
                <div className={styles.modal} onClick={() => setShowTextUpload(false)}>
                    <div className={`${styles.modalContent} ${styles.textUploadModal}`} onClick={(e) => e.stopPropagation()}>
                        <h2>Upload Text Content</h2>
                        <p className={styles.reviewNote}>
                            Paste MCQ questions below. Your draft is auto-saved in your browser.
                            Format: Question text followed by options (A, B, C, D, E).
                        </p>
                        <div className={styles.formRow}>
                            <label>
                                Questions Text
                                {textUploadContent && (
                                    <button
                                        type="button"
                                        className={styles.clearDraftButton}
                                        onClick={() => {
                                            if (window.confirm('Clear all text? This cannot be undone.')) {
                                                setTextUploadContent('');
                                                localStorage.removeItem(`qbank_${activeBank}_draft_text`);
                                            }
                                        }}
                                    >
                                        Clear Draft
                                    </button>
                                )}
                            </label>
                            <textarea
                                value={textUploadContent}
                                onChange={(e) => setTextUploadContent(e.target.value)}
                                className={styles.textareaLarge}
                                rows={12}
                                placeholder={`Example format:

Q1. Which material is used for pulp capping?
a) Zinc oxide eugenol
b) Calcium hydroxide *
c) Glass ionomer
d) Composite resin

Q2. The most common site for oral squamous cell carcinoma:
A) Lateral border of tongue ✓
B) Floor of mouth
C) Buccal mucosa
D) Soft palate`}
                            />
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                className={styles.secondaryButton}
                                onClick={() => setShowTextUpload(false)}
                            >
                                Close
                            </button>
                            <button
                                className={styles.primaryButton}
                                onClick={handleTextUpload}
                                disabled={!textUploadContent.trim() || uploading}
                            >
                                {uploading ? "Uploading..." : "Upload & Parse"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Telegram Import Modal */}
            {showTelegramImport && (
                <div className={styles.modal} onClick={() => setShowTelegramImport(false)}>
                    <div className={`${styles.modalContent} ${styles.textUploadModal}`} onClick={(e) => e.stopPropagation()}>
                        <h2>Import from Telegram</h2>

                        {telegramError && (
                            <div className={styles.telegramError}>{telegramError}</div>
                        )}

                        {/* Preview mode */}
                        {tgPreview ? (
                            <>
                                <div className={styles.telegramPreviewHeader}>
                                    <span>{tgPreview.channel_title}</span>
                                    <span>{tgPreview.messages_count} messages</span>
                                    <span>{Math.round(tgPreview.text_length / 1024)} KB</span>
                                </div>

                                {tgPreview.skipped && Object.keys(tgPreview.skipped).length > 0 && (
                                    <p className={styles.reviewNote}>
                                        Skipped: {Object.entries(tgPreview.skipped).map(([k, v]) =>
                                            `${v} ${k.replace(/_/g, ' ')}`
                                        ).join(', ')}
                                    </p>
                                )}

                                {tgPreview.text_length > 80 * 1024 && (
                                    <div className={styles.telegramWarning}>
                                        Large content ({Math.round(tgPreview.text_length / 1024)} KB). Consider narrowing the date range for better processing.
                                    </div>
                                )}

                                <div className={styles.formRow}>
                                    <label>
                                        Messages Preview (editable)
                                        {tgPreviewText && (
                                            <button
                                                type="button"
                                                className={styles.clearDraftButton}
                                                onClick={() => {
                                                    if (window.confirm('Reset to original fetched content?')) {
                                                        setTgPreviewText(tgPreview.text_content || "");
                                                    }
                                                }}
                                            >
                                                Reset
                                            </button>
                                        )}
                                    </label>
                                    <textarea
                                        value={tgPreviewText}
                                        onChange={(e) => setTgPreviewText(e.target.value)}
                                        className={styles.textareaLarge}
                                        rows={14}
                                    />
                                </div>

                                <div className={styles.modalActions}>
                                    <button
                                        className={styles.secondaryButton}
                                        onClick={() => { setTgPreview(null); setTgPreviewText(""); localStorage.removeItem(`qbank_${activeBank}_telegram_draft`); }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleTelegramSubmit}
                                        disabled={!tgPreviewText.trim() || uploading}
                                    >
                                        {uploading ? "Submitting..." : "Submit for Processing"}
                                    </button>
                                </div>
                            </>
                        ) : !telegramSession || telegramSession.auth_status === "failed" ? (
                            /* Auth setup form */
                            <>
                                <p className={styles.reviewNote}>
                                    Connect your Telegram account to import messages from channels. Get your API credentials from{" "}
                                    <a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer">my.telegram.org</a>.
                                    This is a one-time setup.
                                </p>
                                <div className={styles.formRow}>
                                    <label>API ID</label>
                                    <input
                                        type="text"
                                        value={tgApiId}
                                        onChange={(e) => setTgApiId(e.target.value)}
                                        placeholder="12345678"
                                        className={styles.input}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <label>API Hash</label>
                                    <input
                                        type="text"
                                        value={tgApiHash}
                                        onChange={(e) => setTgApiHash(e.target.value)}
                                        placeholder="0123456789abcdef..."
                                        className={styles.input}
                                    />
                                </div>
                                <div className={styles.formRow}>
                                    <label>Phone Number</label>
                                    <input
                                        type="text"
                                        value={tgPhone}
                                        onChange={(e) => setTgPhone(e.target.value)}
                                        placeholder="+966xxxxxxxxx"
                                        className={styles.input}
                                    />
                                </div>
                                <div className={styles.modalActions}>
                                    <button className={styles.secondaryButton} onClick={() => setShowTelegramImport(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleTelegramAuth}
                                        disabled={telegramLoading || !tgApiId || !tgApiHash || !tgPhone}
                                    >
                                        {telegramLoading ? "Sending..." : "Send Code"}
                                    </button>
                                </div>
                            </>
                        ) : telegramSession.auth_status === "awaiting_code" ? (
                            /* Code verification */
                            <>
                                <p className={styles.reviewNote}>
                                    A verification code was sent to your Telegram app. Enter it below.
                                </p>
                                <div className={styles.formRow}>
                                    <label>Verification Code</label>
                                    <input
                                        type="text"
                                        value={tgCode}
                                        onChange={(e) => setTgCode(e.target.value)}
                                        placeholder="12345"
                                        className={styles.input}
                                        autoFocus
                                    />
                                </div>
                                <div className={styles.modalActions}>
                                    <button className={styles.secondaryButton} onClick={() => setShowTelegramImport(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleTelegramVerifyCode}
                                        disabled={telegramLoading || !tgCode}
                                    >
                                        {telegramLoading ? "Verifying..." : "Verify Code"}
                                    </button>
                                </div>
                            </>
                        ) : telegramSession.auth_status === "awaiting_2fa" ? (
                            /* 2FA password */
                            <>
                                <p className={styles.reviewNote}>
                                    Your account has two-factor authentication enabled. Enter your 2FA password.
                                </p>
                                <div className={styles.formRow}>
                                    <label>2FA Password</label>
                                    <input
                                        type="password"
                                        value={tg2faPassword}
                                        onChange={(e) => setTg2faPassword(e.target.value)}
                                        placeholder="Your 2FA password"
                                        className={styles.input}
                                        autoFocus
                                    />
                                </div>
                                <div className={styles.modalActions}>
                                    <button className={styles.secondaryButton} onClick={() => setShowTelegramImport(false)}>
                                        Cancel
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleTelegramVerify2FA}
                                        disabled={telegramLoading || !tg2faPassword}
                                    >
                                        {telegramLoading ? "Verifying..." : "Verify Password"}
                                    </button>
                                </div>
                            </>
                        ) : telegramSession.auth_status === "authenticated" ? (
                            /* Import form - authenticated */
                            <>
                                <div className={styles.telegramConnected}>
                                    <span className={styles.connectedBadge}>
                                        Connected as @{telegramSession.telegram_username || "user"}
                                    </span>
                                    <button
                                        className={styles.clearDraftButton}
                                        onClick={handleTelegramDisconnect}
                                        disabled={telegramLoading}
                                    >
                                        Disconnect
                                    </button>
                                </div>

                                <div className={styles.formRow}>
                                    <label>Channel</label>
                                    <div className={styles.channelInputRow}>
                                        <input
                                            type="text"
                                            value={tgChannel}
                                            onChange={(e) => { setTgChannel(e.target.value); setTgChannelInfo(null); }}
                                            onBlur={() => { if (tgChannel.trim()) handleResolveChannel(); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && tgChannel.trim()) handleResolveChannel(); }}
                                            placeholder="@channel_name"
                                            className={styles.input}
                                        />
                                        {tgChannelInfo && (
                                            <span className={styles.channelInfo}>
                                                {tgChannelInfo.title}
                                                {tgChannelInfo.members_count != null && ` (${tgChannelInfo.members_count.toLocaleString()} members)`}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <label>Date Range</label>
                                    <div className={styles.dateRangeRow}>
                                        <input
                                            type="date"
                                            value={tgDateFrom}
                                            onChange={(e) => setTgDateFrom(e.target.value)}
                                            className={styles.input}
                                        />
                                        <span>to</span>
                                        <input
                                            type="date"
                                            value={tgDateTo}
                                            onChange={(e) => setTgDateTo(e.target.value)}
                                            className={styles.input}
                                        />
                                    </div>
                                </div>

                                <div className={styles.modalActions}>
                                    <button className={styles.secondaryButton} onClick={() => setShowTelegramImport(false)}>
                                        Close
                                    </button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={handleFetchMessages}
                                        disabled={telegramLoading || !tgChannel.trim() || !tgDateFrom || !tgDateTo}
                                    >
                                        {telegramLoading ? "Fetching..." : "Fetch Messages"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Loading / unknown state */
                            <div className={styles.modalActions}>
                                <button className={styles.secondaryButton} onClick={() => setShowTelegramImport(false)}>
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {reviewingUpload && (
                <div className={styles.modal} onClick={() => setReviewingUpload(null)}>
                    <div
                        className={`${styles.modalContent} ${styles.reviewModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2>Review Questions ({editableQuestions.length})</h2>
                        <p className={styles.reviewNote}>
                            Edit questions below, then approve to create them. Agent4 will determine the correct answer using textbook evidence.
                        </p>

                        <div className={styles.reviewList}>
                            {editableQuestions.map((q, idx) => (
                                <div key={idx} className={styles.reviewQuestion}>
                                    <div className={styles.reviewHeader}>
                                        <span>Q{idx + 1}</span>
                                        <select
                                            value={q.specialty}
                                            onChange={(e) => updateQuestion(idx, "specialty", e.target.value)}
                                            className={styles.selectSmall}
                                        >
                                            {specialties.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={q.difficulty}
                                            onChange={(e) => updateQuestion(idx, "difficulty", e.target.value)}
                                            className={styles.selectSmall}
                                        >
                                            <option value="easy">Easy</option>
                                            <option value="medium">Medium</option>
                                            <option value="hard">Hard</option>
                                        </select>
                                        {/* Note: Correct answer is determined by Agent4 with evidence */}
                                    </div>
                                    <textarea
                                        value={q.question_text}
                                        onChange={(e) => updateQuestion(idx, "question_text", e.target.value)}
                                        className={styles.textareaQuestion}
                                        rows={2}
                                    />
                                    {q.options.map((opt, optIdx) => (
                                        <div key={optIdx} className={styles.optionRow}>
                                            <span className={styles.optionLabel}>{opt.label}</span>
                                            <input
                                                type="text"
                                                value={opt.text}
                                                onChange={(e) => {
                                                    const newOptions = [...q.options];
                                                    newOptions[optIdx] = { ...opt, text: e.target.value };
                                                    updateQuestion(idx, "options", newOptions);
                                                }}
                                                className={styles.inputOption}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                className={styles.secondaryButton}
                                onClick={() => setReviewingUpload(null)}
                            >
                                Cancel
                            </button>
                            <button className={styles.primaryButton} onClick={approveQuestions}>
                                Approve & Process ({editableQuestions.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Question Modal (for search results) */}
            {editingQuestion && (
                <div className={styles.modal} onClick={closeEditModal}>
                    <div className={`${styles.modalContent} ${styles.editQuestionModal}`} onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Question</h2>

                        <div className={styles.formRow}>
                            <label>Question Text</label>
                            <textarea
                                className={styles.textareaLarge}
                                value={editForm.question_text}
                                onChange={(e) => setEditForm({ ...editForm, question_text: e.target.value })}
                                rows={3}
                            />
                        </div>

                        <div className={styles.formRow}>
                            <label>Options (select correct answer)</label>
                            {editForm.options.map((opt, idx) => (
                                <div key={opt.label} className={styles.optionEditRow}>
                                    <span className={styles.optionEditLabel}>{opt.label}</span>
                                    <input
                                        type="text"
                                        className={styles.optionEditInput}
                                        value={opt.text}
                                        onChange={(e) => {
                                            const newOptions = [...editForm.options];
                                            newOptions[idx] = { ...opt, text: e.target.value };
                                            setEditForm({ ...editForm, options: newOptions });
                                        }}
                                    />
                                    <input
                                        type="radio"
                                        className={styles.correctRadio}
                                        name="correct_answer"
                                        checked={editForm.correct_answer === opt.label}
                                        onChange={() => setEditForm({ ...editForm, correct_answer: opt.label })}
                                        title="Mark as correct"
                                    />
                                </div>
                            ))}
                        </div>

                        <div className={styles.formRowInline}>
                            <div className={styles.formRow}>
                                <label>Specialty</label>
                                <select
                                    className={styles.select}
                                    value={editForm.specialty}
                                    onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })}
                                >
                                    {specialties.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formRow}>
                                <label>Difficulty</label>
                                <select
                                    className={styles.select}
                                    value={editForm.difficulty}
                                    onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value })}
                                >
                                    <option value="easy">Easy</option>
                                    <option value="medium">Medium</option>
                                    <option value="hard">Hard</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <label>Explanation</label>
                            <textarea
                                className={styles.textareaLarge}
                                value={editForm.explanation}
                                onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                                rows={5}
                            />
                        </div>

                        <div className={styles.formRow}>
                            <label>References</label>
                            <QBankReferenceEditor
                                references={editForm.question_references}
                                onChange={(question_references) => setEditForm({
                                    ...editForm,
                                    question_references,
                                })}
                            />
                        </div>

                        <div className={styles.modalActions}>
                            <button className={styles.secondaryButton} onClick={closeEditModal}>
                                Cancel
                            </button>
                            <button
                                className={styles.primaryButton}
                                onClick={saveQuestion}
                                disabled={savingQuestion}
                            >
                                {savingQuestion ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQBank;
